import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MessageSquare, Paperclip, Calendar, CheckCircle2, Circle } from "lucide-react";
import type { Task } from "@/features/tasks/types/task";
import { CategoryBadge } from "@/features/tasks/components/CategoryBadge";
import { ProfileAvatar } from "@/shared/components/ui/Avatar";

interface TaskCardProps {
  task: Task;
  onSelect: (task: Task) => void;
  onEdit?: (task: Task) => void;
  onComplete?: (task: Task) => void;
}

function getDeadlineStyle(deadline: string | undefined): {
  label: string;
  className: string;
} | null {
  if (!deadline) return null;
  const now = new Date();
  const due = new Date(deadline);
  const diffMs = due.getTime() - now.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffDays < 0) {
    return { label: "Overdue", className: "text-red-600" };
  }
  if (diffDays < 1) {
    return { label: "Due Today", className: "text-red-500" };
  }
  if (diffDays <= 3) {
    return { label: due.toLocaleDateString(), className: "text-amber-600" };
  }
  return { label: due.toLocaleDateString(), className: "text-green-600" };
}

export const TaskCard: React.FC<TaskCardProps> = ({ task, onSelect, onComplete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isCompleted = !!task.completed_at;
  const deadlineInfo = getDeadlineStyle(task.deadline);
  const visibleAssignees = task.assignees.slice(0, 3);
  const extraAssignees = task.assignees.length - 3;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(task);
  };

  const handleComplete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onComplete?.(task);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`bg-white rounded-lg shadow-sm border p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-all ${
        isCompleted
          ? "border-green-200 bg-green-50/40"
          : "border-gray-200 hover:border-indigo-200"
      } ${isDragging ? "shadow-lg ring-2 ring-indigo-300" : ""}`}
      onClick={handleClick}
    >
      <div className="flex items-start gap-2">
        {/* Completion circle */}
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={handleComplete}
          className="mt-0.5 shrink-0 text-gray-300 hover:text-green-500 transition-colors"
          title={isCompleted ? "Mark incomplete" : "Mark complete"}
        >
          {isCompleted ? (
            <CheckCircle2 className="w-4 h-4 text-green-500" />
          ) : (
            <Circle className="w-4 h-4" />
          )}
        </button>

        <div className="flex-1 min-w-0">
          {/* Category */}
          {task.category && (
            <div className="mb-1.5">
              <CategoryBadge category={task.category} />
            </div>
          )}

          {/* ID + Title */}
          <p className="text-[10px] text-gray-400 font-mono mb-0.5">#{task.row_id}</p>
          <p className={`text-sm font-medium leading-snug line-clamp-2 ${
            isCompleted ? "line-through text-gray-400" : "text-gray-900"
          }`}>
            {task.title}
          </p>

          {/* Deadline */}
          {deadlineInfo && !isCompleted && (
            <div className={`flex items-center gap-1 mt-1.5 text-xs ${deadlineInfo.className}`}>
              <Calendar className="w-3 h-3 shrink-0" />
              <span>{deadlineInfo.label}</span>
            </div>
          )}

          {/* Footer: assignees + counts */}
          <div className="flex items-center justify-between mt-2.5">
            {/* Assignee avatars */}
            <div className="flex items-center -space-x-1.5">
              {visibleAssignees.map((a) => (
                <div key={a.user_id} className="ring-2 ring-white rounded-full">
                  <ProfileAvatar
                    picId={a.profile_pic_id}
                    firstName={a.first_name}
                    lastName={a.last_name}
                    size="sm"
                  />
                </div>
              ))}
              {extraAssignees > 0 && (
                <div className="ring-2 ring-white rounded-full w-8 h-8 bg-gray-100 text-gray-600 text-xs flex items-center justify-center font-medium">
                  +{extraAssignees}
                </div>
              )}
            </div>

            {/* Counts */}
            <div className="flex items-center gap-2 text-gray-400 text-xs">
              {task.comment_count > 0 && (
                <span className="flex items-center gap-0.5">
                  <MessageSquare className="w-3 h-3" />
                  {task.comment_count}
                </span>
              )}
              {task.attachment_count > 0 && (
                <span className="flex items-center gap-0.5">
                  <Paperclip className="w-3 h-3" />
                  {task.attachment_count}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
